import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

/**
 * Batch modify dialog — takes a set of selected product IDs and lets the
 * user set fixed values for stock / cost_price / unit_price / category /
 * description / dzi_category / visible_on_shop. Only fields whose "启用" checkbox
 * is on are sent to the backend.
 */
const emptyState = {
    stock: { on: false, value: "" },
    cost_price: { on: false, value: "" },
    unit_price: { on: false, value: "" },
    category: { on: false, value: "" },
    description: { on: false, value: "" },
    dzi_category: { on: false, value: "other" },
    visible_on_shop: { on: false, value: true },
    featured_crystal: { on: false, value: true },
    featured_jade: { on: false, value: true },
};

export default function BulkUpdateProductsDialog({
    open,
    onOpenChange,
    productIds = [],
    dziOptions = [],
    onDone,
}) {
    const [state, setState] = useState(emptyState);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if (open) setState(emptyState); }, [open]);

    const setField = (key, patch) => setState((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

    const enabledCount = Object.values(state).filter((f) => f.on).length;

    const submit = async () => {
        if (productIds.length === 0) { toast.error("请先勾选产品"); return; }
        const updates = {};
        if (state.stock.on) updates.stock = Math.max(0, parseInt(state.stock.value || "0", 10) || 0);
        if (state.cost_price.on) updates.cost_price = Math.max(0, parseFloat(state.cost_price.value || "0") || 0);
        if (state.unit_price.on) updates.unit_price = Math.max(0, parseFloat(state.unit_price.value || "0") || 0);
        if (state.category.on) updates.category = (state.category.value || "").trim();
        if (state.description.on) updates.description = (state.description.value || "").trim();
        if (state.dzi_category.on) updates.dzi_category = state.dzi_category.value || "other";
        if (state.visible_on_shop.on) updates.visible_on_shop = !!state.visible_on_shop.value;
        if (state.featured_crystal.on) updates.featured_crystal = !!state.featured_crystal.value;
        if (state.featured_jade.on) updates.featured_jade = !!state.featured_jade.value;
        if (Object.keys(updates).length === 0) { toast.error("请至少启用一个要修改的字段"); return; }

        setSubmitting(true);
        try {
            const { data } = await api.post("/products/bulk-update", { product_ids: productIds, updates });
            toast.success(`已更新 ${data.updated} 件产品`);
            onOpenChange(false);
            onDone?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "批量修改失败");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="bulk-update-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-gold-600" />
                        批量修改产品
                    </DialogTitle>
                    <div className="text-xs text-gold-600 pt-1">
                        将同时应用到 <span className="font-mono font-bold text-slate-900">{productIds.length}</span> 件已选产品 · 勾选左侧「启用」的字段才会被覆盖
                    </div>
                </DialogHeader>

                <div className="space-y-3 pt-2 max-h-[70vh] overflow-y-auto">
                    <BulkRow
                        testId="bulk-update-stock"
                        label="库存"
                        on={state.stock.on}
                        onToggle={(v) => setField("stock", { on: v })}
                    >
                        <Input
                            type="number"
                            min="0"
                            step="1"
                            disabled={!state.stock.on}
                            value={state.stock.value}
                            onChange={(e) => setField("stock", { value: e.target.value })}
                            placeholder="全部设为这个数量"
                            className="font-mono"
                        />
                    </BulkRow>

                    <BulkRow
                        testId="bulk-update-cost"
                        label="成本价 (RM)"
                        on={state.cost_price.on}
                        onToggle={(v) => setField("cost_price", { on: v })}
                    >
                        <Input
                            type="number"
                            min="0"
                            step="0.01"
                            disabled={!state.cost_price.on}
                            value={state.cost_price.value}
                            onChange={(e) => setField("cost_price", { value: e.target.value })}
                            placeholder="全部设为这个价格"
                            className="font-mono"
                        />
                    </BulkRow>

                    <BulkRow
                        testId="bulk-update-price"
                        label="售价 (RM)"
                        on={state.unit_price.on}
                        onToggle={(v) => setField("unit_price", { on: v })}
                    >
                        <Input
                            type="number"
                            min="0"
                            step="0.01"
                            disabled={!state.unit_price.on}
                            value={state.unit_price.value}
                            onChange={(e) => setField("unit_price", { value: e.target.value })}
                            placeholder="全部设为这个售价"
                            className="font-mono"
                        />
                    </BulkRow>

                    <BulkRow
                        testId="bulk-update-category"
                        label="分类"
                        on={state.category.on}
                        onToggle={(v) => setField("category", { on: v })}
                    >
                        <Input
                            disabled={!state.category.on}
                            value={state.category.value}
                            onChange={(e) => setField("category", { value: e.target.value })}
                            placeholder="如：项链、耳环（留空清除）"
                        />
                    </BulkRow>

                    <BulkRow
                        testId="bulk-update-description"
                        label="产品描述"
                        on={state.description.on}
                        onToggle={(value) => setField("description", { on: value })}
                    >
                        <Textarea
                            data-testid="bulk-update-description-input"
                            disabled={!state.description.on}
                            value={state.description.value}
                            onChange={(event) => setField("description", {
                                value: event.target.value,
                            })}
                            placeholder="统一覆盖描述内容（留空可清除全部已选商品的描述）"
                            className="min-h-[76px] resize-y"
                            maxLength={5000}
                        />
                        <p className="mt-1 text-[10px] text-gold-600">
                            勾选后会将此内容覆盖到所有已选商品。
                        </p>
                    </BulkRow>

                    <BulkRow
                        testId="bulk-update-dzi"
                        label="眼数分类"
                        on={state.dzi_category.on}
                        onToggle={(v) => setField("dzi_category", { on: v })}
                    >
                        <Select
                            value={state.dzi_category.value}
                            onValueChange={(v) => setField("dzi_category", { value: v })}
                            disabled={!state.dzi_category.on}
                        >
                            <SelectTrigger data-testid="bulk-update-dzi-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {dziOptions.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </BulkRow>

                    <BulkRow
                        testId="bulk-update-visible"
                        label="落地页显示"
                        on={state.visible_on_shop.on}
                        onToggle={(v) => setField("visible_on_shop", { on: v })}
                    >
                        <Select
                            value={state.visible_on_shop.value ? "true" : "false"}
                            onValueChange={(v) => setField("visible_on_shop", { value: v === "true" })}
                            disabled={!state.visible_on_shop.on}
                        >
                            <SelectTrigger data-testid="bulk-update-visible-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="true">上架显示</SelectItem>
                                <SelectItem value="false">隐藏（不上架）</SelectItem>
                            </SelectContent>
                        </Select>
                    </BulkRow>

                    <BulkRow
                        testId="bulk-update-featured-crystal"
                        label="精选水晶"
                        on={state.featured_crystal.on}
                        onToggle={(v) => setField("featured_crystal", { on: v })}
                    >
                        <Select
                            value={state.featured_crystal.value ? "true" : "false"}
                            onValueChange={(v) => setField("featured_crystal", { value: v === "true" })}
                            disabled={!state.featured_crystal.on}
                        >
                            <SelectTrigger data-testid="bulk-update-featured-crystal-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="true">加入精选水晶</SelectItem>
                                <SelectItem value="false">移出精选水晶</SelectItem>
                            </SelectContent>
                        </Select>
                    </BulkRow>

                    <BulkRow
                        testId="bulk-update-featured-jade"
                        label="精选翡翠"
                        on={state.featured_jade.on}
                        onToggle={(v) => setField("featured_jade", { on: v })}
                    >
                        <Select
                            value={state.featured_jade.value ? "true" : "false"}
                            onValueChange={(v) => setField("featured_jade", { value: v === "true" })}
                            disabled={!state.featured_jade.on}
                        >
                            <SelectTrigger data-testid="bulk-update-featured-jade-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="true">加入精选翡翠</SelectItem>
                                <SelectItem value="false">移出精选翡翠</SelectItem>
                            </SelectContent>
                        </Select>
                    </BulkRow>
                </div>

                <DialogFooter className="pt-4 flex items-center justify-between gap-2">
                    <div className="text-xs text-gold-500 flex-1">
                        {enabledCount === 0 ? "尚未启用任何字段" : `将修改 ${enabledCount} 个字段`}
                    </div>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
                    <Button
                        data-testid="submit-bulk-update-btn"
                        onClick={submit}
                        disabled={submitting || enabledCount === 0 || productIds.length === 0}
                        className="bg-gold-600 hover:bg-gold-700 text-white"
                    >
                        {submitting ? "更新中..." : `确认修改 ${productIds.length} 件`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BulkRow({ testId, label, on, onToggle, children }) {
    return (
        <div className={`p-3 rounded-md border transition-colors ${on ? "border-gold-300 bg-gold-50/50" : "border-slate-200"}`}>
            <div className="flex items-start gap-3">
                <Checkbox
                    data-testid={`${testId}-toggle`}
                    checked={on}
                    onCheckedChange={(v) => onToggle(!!v)}
                    className="mt-2 data-[state=checked]:bg-gold-600 data-[state=checked]:border-gold-600"
                />
                <Label className={`text-sm w-24 shrink-0 mt-2 ${on ? "text-slate-900 font-semibold" : "text-slate-500"}`}>{label}</Label>
                <div className="flex-1 min-w-0">{children}</div>
            </div>
        </div>
    );
}
